'use client'

import { useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { createClient } from '@/shared/lib/supabase/client'
import { getQueryClient, USER_QUERY_KEY } from '@/shared/lib/queryClient'

export function QueryProvider({ children }: { children: React.ReactNode }) {
  // The browser singleton — same instance store actions reach via
  // getCurrentUser(), so the React tree and imperative code share one cache.
  const queryClient = getQueryClient()

  // Single source of truth for auth changes. Previously every useAuth() mount
  // wired its own onAuthStateChange subscription; now one listener pushes the
  // user straight into the shared cache, so sign-in / sign-out / token refresh
  // update every consumer without anyone firing a network getUser().
  useEffect(() => {
    const supabase = createClient()
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        // Deferred a tick: supabase-js holds its auth lock while dispatching
        // this callback, so queries that fire in reaction to the user landing
        // in the cache (e.g. the unread-feedback RPC) would go out with the
        // pre-sign-in (anon) token and silently get RLS-empty results.
        setTimeout(() => {
          queryClient.setQueryData(USER_QUERY_KEY, session?.user ?? null)
        }, 0)
      },
    )
    return () => subscription.unsubscribe()
  }, [queryClient])

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
